// useProposedActions — fetches Haiku-ranked next-move suggestions for a thread.
//
// Endpoint: GET /api/ai/propose/:conversationId
// Returns 3 ranked actions. The first is tagged "primary" (RECOMMENDED).
//
// Response shape (matches backend):
//   { proposals: [{ id, title, description, preview, is_primary, side_effects[] }],
//     model, cached, generated_at }
//
// Caching:
//   - Server caches per-thread until a new inbound message arrives (that
//     invalidates the cache, next call regenerates).
//   - Client staleTime = 5 min so switching back and forth doesn't re-fetch.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '@/lib/api';

export interface ProposedAction {
  id: string;
  title: string;
  description: string;
  /** The pre-drafted reply body that will be sent if Kimi picks this action. */
  preview: string;
  is_primary: boolean;
  /**
   * Structured side-effects that fire in addition to sending the preview.
   * Known kinds (backend-defined):
   *   { kind: 'set_status', status: 'awaiting' | 'resolved' }
   *   { kind: 'snooze', until: ISO }
   *   { kind: 'add_label', label: string }
   *   { kind: 'add_shopify_order_note', note: string }
   *   { kind: 'none' }  — for "just send the reply"
   */
  side_effects: Array<Record<string, unknown>>;
}

export interface ProposedActionsResponse {
  proposals: ProposedAction[];
  model: string;
  cached: boolean;
  generated_at: string;
  /** Human-readable summary of what Haiku used to make the proposals. */
  context_note: string;
}

export function useProposedActions(conversationId: string | undefined) {
  return useQuery<ProposedActionsResponse>({
    queryKey: ['propose', conversationId],
    queryFn: () => apiGet<ProposedActionsResponse>(`/api/ai/propose/${conversationId}`),
    enabled: !!conversationId,
    staleTime: 5 * 60_000,
    retry: 0,
  });
}

/** Fires an action: sends the preview as the reply + executes side effects. */
export interface ExecuteActionResponse {
  ok: true;
  messageId: string | null;
  appliedSideEffects: string[];
}
export function useExecuteAction(conversationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<
    ExecuteActionResponse,
    Error,
    { actionId: string; editedPreview?: string }
  >({
    mutationFn: (vars) =>
      apiPost<ExecuteActionResponse, { action_id: string; edited_preview?: string }>(
        `/api/ai/propose/${conversationId}/execute`,
        {
          action_id: vars.actionId,
          edited_preview: vars.editedPreview,
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
      void qc.invalidateQueries({ queryKey: ['propose', conversationId] });
    },
  });
}
